<script setup>
import moment from 'moment-timezone';
import { computed, ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { MODAL_MODES, VIEW_TYPES, DRAFT_STATUSES } from './constants';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { generateTempCaseId } from 'shared/helpers/messageVariablesHelper';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import FileIcon from 'dashboard/components-next/icon/FileIcon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

import InboxSelector from './form/InboxSelector.vue';
import ContactSelector from './form/ContactSelector.vue';
import CannedResponseModal from './form/CannedResponseModal.vue';
import SetScheduleModal from './form/SetScheduleModal.vue';
import HtmlEditor from './form/HtmlEditor.vue';

const props = defineProps({
  draftId: {
    type: [Number, String],
    required: false,
    default: null,
  },
  mode: {
    type: String,
    required: false,
    default: VIEW_TYPES.PAGE,
  },
});

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { updateUISettings } = useUISettings();

const htmlEditor = ref(null);
const selectedInbox = ref(null);
const cannedId = ref(null);
const showCC = ref(false);
const showBCC = ref(false);
const showScheduleModal = ref(false);
const toContacts = ref([]);
const ccContacts = ref([]);
const bccContacts = ref([]);
const preloadContacts = ref([]);
const attachedFiles = ref([]);
const subject = ref('');
const messageContent = ref('');
const inboxSignature = ref('');
const useSignature = ref(true);
const autoSaveRemainingTime = ref(-1);
const autoSaveStatus = ref('loading');
const selectTemplate = ref(false);
const scheduledAt = ref(null);
const timezone = ref(null);
const currentDraft = ref({});

const currentDraftId = props.draftId || route.params.id;
const autoSaveDelaySeconds = 5;

// Stable temporary case_id for this draft session. Generated once when
// inbox is first known; restored from draft.meta.case_id on reload.
const tempCaseId = ref('');

const inboxes = useMapGetter('inboxes/getInboxes');

const handleInboxChange = inbox => {
  selectedInbox.value = inbox;
  inboxSignature.value = inbox?.signature || '';
  if (inbox?.id && !tempCaseId.value) {
    tempCaseId.value = generateTempCaseId(inbox.id);
  }
};

const localTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const handleAttachClick = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.onchange = () => {
    const files = Array.from(input.files);
    store
      .dispatch('drafts/attachFiles', {
        draftId: currentDraftId,
        files,
      })
      .then(newFiles => {
        attachedFiles.value = newFiles;
      });
  };
  input.click();
};

const handleRemoveFile = file => {
  /* eslint-disable no-alert */
  const isConfirmed = window.confirm(
    t('DRAFTS.FORM.ATTACHMENTS.REMOVE_FILE_CONFIRMATION', {
      name: file.filename,
    })
  );
  /* eslint-enable no-alert */
  if (!isConfirmed) return;

  store.dispatch('drafts/removeFile', {
    draftId: currentDraftId,
    fileId: file.id,
  });

  attachedFiles.value = attachedFiles.value.filter(f => f.id !== file.id);
};

const handleDownloadFile = file => {
  window.open(file.url, '_blank');
};

const openConversation = () => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: currentDraft.value.conversation.id,
    })
  );
  router.push({ path });
};

const gotoConversation = () => {
  openConversation();

  if (props.mode === VIEW_TYPES.MODAL) {
    updateUISettings({
      draft_compose_mode: MODAL_MODES.MINIMIZED,
    });
  }
};

const onInsertTemplateContent = (htmlContent, selectedCannedId) => {
  htmlEditor.value.insertContent(htmlContent);
  cannedId.value = selectedCannedId;
};

// Filter inboxes to only include email type for selection
const selectableInboxes = computed(() => {
  return inboxes.value.filter(
    inbox => inbox.channel_type === INBOX_TYPES.EMAIL
  );
});

const editable = computed(() => {
  return scheduledAt.value === null;
});

const draftData = computed(() => {
  const toEmail = toContacts.value[0];
  const ccEmails = ccContacts.value.length ? ccContacts.value : [];
  const bccEmails = bccContacts.value.length ? bccContacts.value : [];

  return {
    inbox_id: selectedInbox.value ? selectedInbox.value.id : null,
    content: messageContent.value,
    meta: {
      subject: subject.value,
      to_email: toEmail?.email || toEmail,
      cc_emails: ccEmails.map(contact => contact?.email || contact),
      bcc_emails: bccEmails.map(contact => contact?.email || contact),
      use_signature: useSignature.value,
      canned_response_id: cannedId.value,
      case_id: tempCaseId.value || undefined,
    },
  };
});

const sendable = computed(() => {
  return (
    selectedInbox.value &&
    toContacts.value.length > 0 &&
    messageContent.value.trim().length > 0 &&
    subject.value.trim().length > 0 &&
    autoSaveStatus.value === 'saved'
  );
});

const saveDraft = async () => {
  autoSaveStatus.value = 'saving';
  currentDraft.value = await store.dispatch('drafts/update', {
    id: currentDraftId,
    ...draftData.value,
  });
  setTimeout(() => {
    autoSaveStatus.value = 'saved';
  }, 1000);
};

const manualSaveDraft = () => {
  autoSaveRemainingTime.value = 0;
  saveDraft();
};

const scheduledAtText = computed(() => {
  if (!scheduledAt.value) return '';

  return moment(scheduledAt.value)
    .tz(timezone.value || localTimezone())
    .format('MMM DD, HH:mm A');
});

const redirectToHome = () => {
  router.push({
    name: 'home',
    params: { accountId: route.params.accountId },
  });
};

const onSetSchedule = async (timeZone, scheduledTime) => {
  try {
    showScheduleModal.value = false;
    const scheduledDraft = await store.dispatch('drafts/scheduleSend', {
      id: currentDraftId,
      timeZone: timeZone,
      scheduledAt: scheduledTime.toLocaleString('sv-SE', { hour12: false }),
    });
    scheduledAt.value = scheduledDraft.scheduled_at;
    timezone.value = scheduledDraft.timezone;
    updateUISettings({
      editing_draft_id: null,
      draft_compose_mode: MODAL_MODES.CLOSED,
    });
    if (currentDraft.value.conversation?.id) {
      openConversation();
    } else {
      redirectToHome();
    }
  } catch (error) {
    useAlert(error.message);
  }
};

const onSchedule = event => {
  onSetSchedule(event.timeZone.value, event.scheduledAt);
};

const onSend = () => {
  const scheduledTime = new Date();
  scheduledTime.setSeconds(scheduledTime.getSeconds() + 10);
  onSetSchedule(localTimezone(), scheduledTime);
};

const loadDraft = async () => {
  const draft = await store.dispatch('drafts/getSingle', currentDraftId);
  currentDraft.value = draft;
  if (!draft.id) {
    useAlert(t('DRAFTS.NOT_FOUND'));
    redirectToHome();
  } else if (draft.status === DRAFT_STATUSES.DELIVERED) {
    useAlert(t('DRAFTS.ALREADY_SENT'));
    if (props.mode === VIEW_TYPES.MODAL) {
      openConversation();
      updateUISettings({
        editing_draft_id: null,
        draft_compose_mode: MODAL_MODES.CLOSED,
      });
    } else {
      redirectToHome();
    }
  } else {
    const toContact = draft.contact || draft.meta?.to_email;
    toContacts.value = toContact ? [toContact] : [];
    selectedInbox.value = draft.inbox;
    inboxSignature.value = draft.inbox?.signature || '';
    messageContent.value = draft.content || '';
    cannedId.value = draft.meta?.canned_response_id || null;
    subject.value = draft.meta?.subject || '';
    preloadContacts.value = draft.meta?.preload_contacts || [];
    tempCaseId.value =
      draft.meta?.case_id ||
      (draft.inbox?.id ? generateTempCaseId(draft.inbox.id) : '');
    ccContacts.value = (draft.meta?.cc_emails || []).map(email => {
      const contact = preloadContacts.value.find(c => c.email === email);
      return contact || email;
    });
    bccContacts.value = (draft.meta?.bcc_emails || []).map(email => {
      const contact = preloadContacts.value.find(c => c.email === email);
      return contact || email;
    });
    showCC.value = ccContacts.value.length > 0;
    showBCC.value = bccContacts.value.length > 0;
    useSignature.value = draft.meta?.use_signature ?? false;
    attachedFiles.value = draft.files || [];
    scheduledAt.value = draft.scheduled_at || null;
    timezone.value = draft.timezone || null;

    if (props.mode === VIEW_TYPES.PAGE) {
      updateUISettings({
        editing_draft_id: currentDraftId,
        draft_compose_mode: MODAL_MODES.MINIMIZED,
      });
    }

    setTimeout(() => {
      autoSaveStatus.value = 'saved';
    }, 500);
  }
};

const onCancelSend = async () => {
  await store.dispatch('drafts/cancelScheduled', currentDraftId);
  loadDraft();
};

onMounted(() => {
  autoSaveStatus.value = 'loading';
  loadDraft();

  setInterval(() => {
    autoSaveRemainingTime.value -= 1;
    if (autoSaveRemainingTime.value === 0) saveDraft();
  }, 1000);
});

watch(
  draftData,
  () => {
    if (autoSaveStatus.value === 'loading') return;

    autoSaveStatus.value = 'typing';
    autoSaveRemainingTime.value = autoSaveDelaySeconds;
  },
  { deep: true }
);
</script>

<template>
  <div class="h-full overflow-y-auto">
    <div class="flex flex-col h-full">
      <div class="flex-grow overflow-y-auto">
        <div class="p-4 pb-0 pe-0 flex flex-col h-full">
          <div class="me-4 border-b border-b-n-weak dark:border-b-n-slate-7">
            <div class="h-auto">
              <InboxSelector
                :inboxes="selectableInboxes"
                :selected="selectedInbox"
                :disabled="!editable"
                @on-change="handleInboxChange"
              />
            </div>
            <div class="h-auto">
              <div class="flex flex-row flex-1">
                <div class="flex-auto">
                  <ContactSelector
                    v-model="toContacts"
                    mode="single"
                    class="flex-1"
                    :label="t('DRAFTS.FORM.CONTACT_SELECTOR.LABEL')"
                    :placeholder="t('DRAFTS.FORM.CONTACT_SELECTOR.PLACEHOLDER')"
                    :disabled="!selectedInbox || !editable"
                  />
                  <ContactSelector
                    v-if="showCC"
                    v-model="ccContacts"
                    class="flex-1"
                    mode="multiple"
                    :label="t('DRAFTS.FORM.BUTTONS.CC')"
                    :disabled="!selectedInbox || !editable"
                    :placeholder="
                      t('DRAFTS.FORM.CONTACT_SELECTOR.CC_PLACEHOLDER')
                    "
                  />
                  <ContactSelector
                    v-if="showBCC"
                    v-model="bccContacts"
                    class="flex-1"
                    mode="multiple"
                    :label="t('DRAFTS.FORM.BUTTONS.BCC')"
                    :disabled="!selectedInbox || !editable"
                    :placeholder="
                      t('DRAFTS.FORM.CONTACT_SELECTOR.BCC_PLACEHOLDER')
                    "
                  />
                </div>
                <div
                  v-if="selectedInbox && editable"
                  class="flex justify-end items-center mt-2"
                >
                  <div v-if="!showCC" class="ml-4 flex-1">
                    <button
                      class="px-4 !py-1 text-sm text-n-slate-12 bg-n-alpha-2 rounded-md hover:bg-n-alpha-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-n-brand"
                      @click="showCC = true"
                    >
                      {{ t('DRAFTS.FORM.BUTTONS.CC') }}
                    </button>
                  </div>
                  <div v-if="!showBCC" class="ml-4 flex-1">
                    <button
                      class="px-4 !py-1 text-sm text-n-slate-12 bg-n-alpha-2 rounded-md hover:bg-n-alpha-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-n-brand"
                      @click="showBCC = true"
                    >
                      {{ t('DRAFTS.FORM.BUTTONS.BCC') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="h-auto">
              <div
                class="flex items-center flex-1 w-full gap-3 px-4 py-1 overflow-y-visible"
              >
                <div class="flex items-center flex-1 w-full gap-3">
                  <label
                    class="w-[5rem] text-end mb-0.5 text-sm font-medium text-n-slate-11 whitespace-nowrap"
                  >
                    {{ t('DRAFTS.FORM.SUBJECT_INPUT.LABEL') }}
                  </label>
                  <div
                    class="flex-auto items-center gap-1.5 truncate ltr:pr-1 rtl:pl-1 h-7 min-w-0"
                  >
                    <InlineInput
                      v-model="subject"
                      custom-input-class="flex-1"
                      :placeholder="t('DRAFTS.FORM.SUBJECT_INPUT.PLACEHOLDER')"
                      :disabled="!selectedInbox || !editable"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <CannedResponseModal
            v-if="editable"
            :show="selectTemplate"
            :inbox-id="selectedInbox?.id"
            :to-email="toContacts[0]?.email || toContacts[0]"
            :temp-case-id="tempCaseId"
            @on-close="selectTemplate = false"
            @on-insert="onInsertTemplateContent"
          />
          <div class="flex-1 items-center flex-auto w-full overflow-y-auto">
            <HtmlEditor
              ref="htmlEditor"
              v-model="messageContent"
              :disabled="!selectedInbox || !editable"
            >
              <template #signature>
                <div
                  v-if="useSignature && inboxSignature.length"
                  v-tooltip.top="t('DRAFTS.SIGNATURE')"
                  class="text-sm text-n-slate-11 p-2 rounded-md w-fit animate-pulse mt-4"
                  v-html="inboxSignature"
                />
              </template>
              <template #attachments>
                <div
                  v-if="attachedFiles.length"
                  class="w-full mt-8 p-3 border-t border-n-weak dark:border-n-slate-7"
                >
                  <h4 class="text-sm font-medium text-n-slate-11 mb-2">
                    {{ t('DRAFTS.FORM.ATTACHMENTS.LABEL') }}
                  </h4>
                  <ul class="space-y-1 flex flex-col">
                    <li
                      v-for="file in attachedFiles"
                      :key="file.id"
                      class="flex items-center border border-n-slate-7 rounded-md px-2 w-fit py-1"
                    >
                      <FileIcon
                        :file-type="file.content_type"
                        class="text-n-slate-11 me-2 w-9 h-9"
                      />
                      <span class="text-n-slate-12 me-2">
                        <p class="mb-0 truncate w-[15rem]">
                          {{ file.filename }}
                        </p>
                        <p class="text-n-slate-9 mb-0 text-xs">
                          {{ file.size }}
                        </p>
                      </span>
                      <button
                        class="text-sm text-red-500 flex justify-center px-3"
                        @click="handleDownloadFile(file)"
                      >
                        <Icon
                          icon="i-lucide-download"
                          class="text-n-slate-11"
                        />
                      </button>
                      <button
                        :disabled="!editable"
                        class="text-sm text-red-500 flex justify-center px-3"
                        @click="handleRemoveFile(file)"
                      >
                        <Icon icon="i-lucide-x" />
                      </button>
                    </li>
                  </ul>
                </div>
              </template>
            </HtmlEditor>
          </div>
        </div>
      </div>
      <div class="flex flex-col flex-shrink-0">
        <div
          class="flex items-center justify-between gap-2 px-3 h-12 border-t border-t-n-weak dark:bg-n-alpha-2"
        >
          <div class="flex items-center justify-center min-w-0">
            <div v-if="editable">
              <button
                v-tooltip.top="t('DRAFTS.FORM.BUTTONS.ATTACH_FILE')"
                class="px-4 cursor-pointer text-n-slate-11 relative hover:text-n-slate-12"
                :class="{ 'text-woot-700': attachedFiles.length > 0 }"
                :disabled="!editable"
                @click="handleAttachClick"
              >
                <Icon icon="i-lucide-paperclip" />
                <span
                  v-if="attachedFiles.length"
                  class="absolute -top-1 right-0 flex h-4 w-4 items-center justify-center rounded-full text-white text-[10px] font-semibold bg-red-300 backdrop-blur-md"
                >
                  {{ attachedFiles.length }}
                </span>
              </button>
              <button
                v-tooltip.top="t('DRAFTS.FORM.BUTTONS.SIGNATURE')"
                :disabled="!inboxSignature.length || !editable"
                class="px-4 cursor-pointer hover:text-n-slate-12"
                :class="{
                  'text-woot-700': useSignature && inboxSignature.length,
                  'text-n-slate-11': !useSignature,
                }"
                @click="useSignature = !useSignature"
              >
                <Icon icon="i-lucide-signature" />
              </button>
              <button
                v-tooltip.top="t('DRAFTS.FORM.BUTTONS.TEMPLATES')"
                class="px-4 cursor-pointer text-n-slate-11 hover:text-n-slate-12"
                :disabled="!editable"
                @click="selectTemplate = true"
              >
                <Icon icon="i-lucide-notebook" />
              </button>
            </div>
            <div v-else>
              <div
                v-if="scheduledAt"
                v-tooltip.top="timezone"
                class="flex items-center justify-center min-w-0 space-x-2"
              >
                <Icon icon="i-lucide-calendar-clock" class="text-n-slate-11" />
                <span class="text-sm text-n-slate-11">
                  {{ scheduledAtText }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex items-center justify-center min-w-0">
            <button
              v-if="currentDraft?.conversation?.id"
              v-tooltip.top="t('DRAFTS.ATTRS.OPEN_CONVERSATION')"
              class="text-n-slate-11 cursor-pointer hover:text-n-slate-12 flex justify-center me-4"
              :disabled="!sendable"
              @click="gotoConversation"
            >
              {{ t('DRAFTS.VIEW_CONVERSATION_DETAIL') }}
            </button>
          </div>

          <div class="flex items-center justify-center min-w-0 space-x-2">
            <div
              v-if="editable"
              class="flex items-center justify-center min-w-0 me-4"
            >
              <div
                v-if="currentDraft.meta?.send_error"
                class="flex items-center justify-center text-red-600 ms-8"
              >
                <Icon
                  icon="i-lucide-alert-circle"
                  class="text-red-600 text-n-slate-11 me-2"
                />
                <span class="text-sm truncate max-w-[15rem]">
                  {{ currentDraft.meta?.send_error }}
                </span>
              </div>
              <span
                v-if="autoSaveStatus === 'saved'"
                class="text-sm text-slate-500 me-2"
              >
                {{ $t('DRAFTS.COMPOSE.AUTO_SAVED_MSG') }}
              </span>
              <button v-if="autoSaveStatus === 'saving'">
                <div
                  class="w-4 h-4 rounded-full border-10 border-t-slate-500 border-t-4 bg-slate-100 animate-spin"
                />
              </button>
              <Icon
                v-else-if="autoSaveStatus === 'saved'"
                icon="i-lucide-cloud-check"
                class="text-slate-500"
              />
              <button
                v-else-if="autoSaveStatus === 'typing'"
                v-tooltip.top="
                  $t('DRAFTS.COMPOSE.AUTO_SAVE_IN_MSG', {
                    count: autoSaveRemainingTime,
                  })
                "
                class="flex justify-center"
                @click="manualSaveDraft"
              >
                <Icon icon="i-lucide-save" class="text-slate-500" />
              </button>
            </div>
            <NextButton
              v-if="editable"
              sm
              :disabled="!sendable"
              @click="showScheduleModal = true"
            >
              {{ $t('DRAFTS.SCHEDULE_BUTTON') }}
            </NextButton>
            <NextButton
              v-if="editable"
              sm
              confirmation
              :label="$t('DRAFTS.SEND_BUTTON')"
              :disabled="!sendable"
              @click="onSend"
            />
            <NextButton
              v-if="scheduledAt"
              ruby
              sm
              :label="$t('DRAFTS.CANCEL_SEND_BUTTON')"
              @click="onCancelSend"
            />
          </div>
        </div>
      </div>
    </div>
    <SetScheduleModal
      :show="showScheduleModal"
      :timezone="timezone"
      :scheduled-at="scheduledAt"
      @on-schedule="onSchedule"
      @on-close="showScheduleModal = false"
    />
  </div>
</template>
